import { type FC, type PropsWithChildren, useMemo } from 'react'

import { AuthenticationFilter } from '@dhl/receiver-authentication'
import { ThirdParty, type ThirdPartyConfig } from '@dhl/third-party'
import { useGetUserCurrentAnonymous } from '@dhl/receiver-user-service-api'

import { getConfig, getTopLevelDomain } from '~/config/index.js'
import { rootPath } from '~/Routes.js'

export const StartUp: FC<PropsWithChildren> = ({ children }) => {
  const {
    data: userData,
    isError: isErrorLoadingUser,
    isLoading: isLoadingUser,
  } = useGetUserCurrentAnonymous({
    query: {
      refetchOnWindowFocus: false,
    },
  })
  const {
    authentication,
    cspNonce,
    datadog,
    environment,
    gtm,
    oneTrust,
    version,
    adobe: { url: adobeTagManagerUrl },
  } = getConfig()

  const thirdPartyConfig: ThirdPartyConfig = {
    oneTrustId: oneTrust.id,
    gmtId: gtm.id,
    virtualPageRootPath: rootPath,
    oneTrustNativeEnabled: oneTrust.enableForNative,
    oneTrustNativeEnabledFromVersion: oneTrust.enableForNativeFromVersion,
    isLoggedIn: true,
    nonce: cspNonce,
    topLevelDomain: getTopLevelDomain(),
    adobeTagManagerUrl,
  }

  const datadogConfig = useMemo(() => {
    if (!isLoadingUser && userData?.user.userId) {
      return {
        environment,
        userId: userData?.user.userId,
        version,
        ...datadog,
      }
    }

    return undefined
  }, [datadog, environment, isLoadingUser, userData?.user.userId, version])

  return (
    <AuthenticationFilter
      redirectOnFailure
      isAuthorized={userData?.user.userType === 'consumer'}
      isErrorLoadingProfile={isErrorLoadingUser}
      isLoadingProfile={isLoadingUser}
      paths={{
        loginPath: authentication.signinPath,
        logoutPath: authentication.logoutPath,
      }}
    >
      <ThirdParty config={thirdPartyConfig} datadogConfig={datadogConfig}>
        {children}
      </ThirdParty>
    </AuthenticationFilter>
  )
}
